<template>
  <div class="task-details-page">
    <Header ref="header" helpURL="studio/tasks/">
      <template #page-slot>
        Task Details
      </template>
      <template #header-actions>
        <IconButton
          icon="arrow-left"
          :width="24"
          :height="24"
          class="back-button"
          @click="goBack"
          title="Back to Tasks"
        />
      </template>
    </Header>
    
    <div class="main-page-div">
      <main class="main-display-area">
        <div v-if="loading" class="loading-state">
          <div class="spinner">Loading task details...</div>
        </div>
        
        <div v-else>
          <!-- Task Card in Wide (List) Mode with Tab Buttons -->
          <div class="task-card-section">
            <TaskCard 
              :session="task"
              :is-list="true"
              :show-icons="false"
            />
            <div class="tab-buttons">
              <SelectCharts 
                v-model:group="selectedGroup"
                :task-id="taskID"
                :chart-groups="chartGroups"
                @switch-tab="activeTab = $event"
                @reload-charts="handleReloadCharts"
              />
              <SelectTrace 
                :task-id="taskID"
                :trace-options="traceList"
                :trace-file="selectedTrace"
                @open-trace="handleOpenTrace"
                @switch-tab="activeTab = $event"
                @reload-trace="handleReloadTrace"
              />
            </div>
          </div>

          <!-- Main Content Area -->
          <div class="content-area" :style="{ height: availableHeight }">
            <Charts v-if="activeTab === 'charts'" :task-id="taskID" :group-name="selectedGroup" :chart-file-names="chartFileNames" @switch-tab="activeTab = $event" />
            <Profiler v-else-if="activeTab === 'profile'" :task-id="taskID" :trace-file="selectedTrace" />
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import Header from '@/components/Header.vue';
import IconButton from '@/components/IconButton.vue';
import TaskCard from '@/components/Apps/TaskCard.vue';
import SelectCharts from '@/components/ProfilerComponents/SelectCharts.vue';
import SelectTrace from '@/components/ProfilerComponents/SelectTrace.vue';
import Charts from '@/components/ProfilerComponents/Charts.vue';
import Profiler from '@/components/ProfilerComponents/Profiler.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';

const router = useRouter();
const route = useRoute();
const $toast = useToast();

const taskUID = ref('');
const taskID = ref(null);
const loading = ref(true);
const task = ref(null);
const activeTab = ref('charts');
const chartGroups = ref([]);
const chartList = ref({});
const traceList = ref([]);
const selectedGroup = ref('');
const selectedTrace = ref('');
const chartFileNames = ref([]);

// Refs for height calculation
const windowHeight = ref(window.innerHeight);
const calculatedHeight = ref('100%');

// Function to calculate available height with fixed dimensions
const calculateAvailableHeight = () => {
  const headerHeight = 70;
  const taskCardHeight = 70;
  const availH = windowHeight.value - headerHeight - taskCardHeight;
  
  if (availH > 0) {
    calculatedHeight.value = `${availH}px`;
    console.log(`✅ Height calculated: ${calculatedHeight.value} (window: ${windowHeight.value} - header: ${headerHeight} - card: ${taskCardHeight})`);
  }
};

const availableHeight = computed(() => calculatedHeight.value);

const goBack = () => {
  router.push('/tasks');
};

const fetchTaskData = async () => {
  try {
    loading.value = true;
    
    // Extract hex part from uid (bt-abc -> abc) and convert to decimal
    const hexPart = taskUID.value.replace('bt-', '');
    taskID.value = parseInt(hexPart, 16);
    
    // Fetch task details from API
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.api("task.get",{'id':taskID.value});

    if ((!response || response.error) && response.result) {
      throw new Error(response?.error || 'Failed to fetch task data');
    }

    task.value = response.result;
    
    // Fetch chart groups
    await fetchChartGroups();
    
    // Fetch trace list
    await fetchTraceList();
  } catch (error) {
    console.error('Error fetching task data:', error);
    
    // Show error toast
    $toast.open({
      queue: true,
      dismissible: true,
      message: error.message || 'Failed to fetch task details',
      type: 'error',
      position: 'top',
    });
    
    // Fallback data on error
    task.value = {
      id: taskUID.value,
      uid: taskUID.value,
      status: 'Error',
      type: 'Unknown',
    };
  } finally {
    loading.value = false;
  }
};

const fetchChartGroups = async () => {
  try {
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.api('task.chart.list', {
      task_id: taskID.value,
    });
    
    if (response && response.result) {
      // Extract chart group names from response
      chartList.value = response.result.data || {};
      chartGroups.value = Object.keys(chartList.value);
    }
  } catch (error) {
    console.error('Error fetching chart groups:', error);
  }
};

const fetchTraceList = async () => {
  try {
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.api('task.data.list', {
      task_id: taskID.value,
    });
    
    if (response && response.result) {
      // response.result is an array of trace file names
      traceList.value = response.result.traces;
    }
  } catch (error) {
    console.error('Error fetching trace list:', error);
  }
};



const handleOpenTrace = (data) => {
  selectedTrace.value = data.trace;
  console.log('Opening trace:', data);
};

const handleReloadTrace = (data) => {
  // Re-trigger trace loading by resetting and reloading
  handleOpenTrace(data);
  console.log('Reloading trace:', data);
};

const handleReloadCharts = async () => {
  if (selectedGroup.value && taskID.value) {
    try {
      // Clear charts first to force re-render
      chartFileNames.value = [];
      
      const dataConnect = new DatastoreConnect();
      const response = await dataConnect.api('task.chart.list', {
        task_id: taskID.value,
        group: selectedGroup.value,
      });
      
      if (response?.result?.data && response.result.data[selectedGroup.value]) {
        // Create new array to ensure Vue detects the change
        chartFileNames.value = [...response.result.data[selectedGroup.value]];
        console.log('✅ Charts reloaded for group:', selectedGroup.value, chartFileNames.value);
      }
    } catch (error) {
      console.error('Error reloading charts:', error);
    }
  }
};

// Watch for loading change to recalculate after content renders
watch(
  () => loading.value,
  (isLoading) => {
    if (!isLoading) {
      calculateAvailableHeight();
    }
  }
);

// Watch for changes to selectedGroup
watch(
  () => selectedGroup.value,
  async (newGroup) => {
    if (newGroup && taskID.value) {
      console.log('📥 Chart group changed:', newGroup);
      chartFileNames.value = chartList.value[newGroup] || [];
    }
  }
);

onMounted(() => {
  // Get task UID from route params
  taskUID.value = route.params.task_uid;
  fetchTaskData();
  
  // Handle window resize events
  const handleResize = () => {
    windowHeight.value = window.innerHeight;
    calculateAvailableHeight();
  };
  window.addEventListener('resize', handleResize);
  
  // Calculate height after initial render
  calculateAvailableHeight();
  
  return () => window.removeEventListener('resize', handleResize);
});
</script>

<style lang="scss" scoped>
.task-details-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.main-page-div {
  flex: 1;
  overflow: hidden;
}

.main-display-area {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  @include themify() {
    background: themed('page-background');
  }
}

// Header styling
::v-deep header {
  height: 70px;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;

  .spinner {
    font-size: 18px;
    @include themify() {
      color: themed('body-text-color');
    }
  }
}

// Task Card Section
.task-card-section {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
  padding-left: 30px;
  height: 70px;
  border-bottom: 2px solid rgb(45, 64, 82);

}

.task-card-wrapper {
  flex: 1;
  min-width: 0;
}

.tab-buttons {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;
  @include themify() {
    border-bottom-color: themed('border-color');
  }
}

.tab-button {
  padding: 10px 16px;
  border: 2px solid #ccc;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  @include themify() {
    color: themed('body-text-color');
    border-color: themed('border-color');
    background: themed('input-background');
  }

  &:hover:not(.active) {
    border-color: #2196f3;
    background: #f5f5f5;
  }

  &.active {
    background: #2196f3;
    color: white;
    border-color: #2196f3;
  }
}

// Content Area
.content-area {
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  @include themify() {
    background: themed('card-color');
  }
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;

  &.active {
    background-color: #4caf50;
    color: white;
  }

  &.completed {
    background-color: #2196f3;
    color: white;
  }

  &.failed {
    background-color: #f44336;
    color: white;
  }

  &.pending {
    background-color: #ff9800;
    color: white;
  }
}

.empty-message {
  padding: 32px;
  text-align: center;
  @include themify() {
    color: themed('body-text-color-secondary');
  }
}

.back-button {
  cursor: pointer;
}
</style>
